import { useEffect, useState } from 'react';
import { Plural, Trans } from '@lingui/react/macro';

import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useBulkConfirmProcessingReception } from '@/models/article';
import { type RequestWithRelationsFromRequestsList } from '@/models/request';
import { TrackingNumberInput } from '@/routes/Requests/Request/Article/components/ArticleActions/shared/TrackingNumberInput';

import { type BulkTaskScope } from '../BulkActionBar/getBulkEligibility';

import { BulkConfirmationDialog } from './BulkConfirmationDialog';

export const BulkConfirmProcessingReceptionDialog = ({
  showDialog,
  setShowDialog,
  scope,
  selectedRequests,
  onSuccess,
}: {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  scope: BulkTaskScope;
  selectedRequests: RequestWithRelationsFromRequestsList[];
  onSuccess: () => void;
}) => {
  const [trackingNumber, setTrackingNumber] = useState<string | undefined>(undefined);
  const { mutateAsync: confirmReception, isPending } = useBulkConfirmProcessingReception();
  const itemCount = scope.articleIds.length;
  const requestCount = scope.eligibleRequests.length;
  const skippedCount = selectedRequests.filter(
    (request) => !scope.eligibleRequests.some((eligible) => eligible.id === request.id)
  ).length;

  useEffect(() => {
    if (showDialog) {
      setTrackingNumber(undefined);
    }
  }, [showDialog]);

  return (
    <BulkConfirmationDialog
      title={<Trans id="article.actions.processing.confirm">Confirm reception</Trans>}
      confirm={<Trans id="article.actions.processing.confirm.button">Confirm</Trans>}
      showDialog={showDialog}
      setShowDialog={setShowDialog}
      isPending={isPending}
      isConfirmDisabled={scope.articleIds.length === 0}
      maxWidth="32rem"
      requests={selectedRequests}
      onCloseAfterResult={onSuccess}
      onConfirm={() =>
        confirmReception({
          articleIds: scope.articleIds,
          trackingNumber: trackingNumber?.trim() || undefined,
        })
      }
    >
      <Stack gap="1rem">
        <Stack gap="0.5rem">
          <Paragraph>
            <Trans id="requests.bulk.confirm-receipt.text">
              By confirming, you acknowledge the reception of the items. This will apply to{' '}
              <Plural value={itemCount} one="# item" other="# items" /> in{' '}
              <Plural value={requestCount} one="# request" other="# requests" />.
            </Trans>
          </Paragraph>
          {skippedCount > 0 && (
            <Message type="neutral">
              <Plural
                id="requests.bulk.confirm-receipt.skip"
                value={skippedCount}
                one="# request has no item to confirm reception for"
                other="# requests have no item to confirm reception for"
              />
            </Message>
          )}
        </Stack>
        <TrackingNumberInput
          trackingNumber={trackingNumber}
          setTrackingNumber={setTrackingNumber}
        />
      </Stack>
    </BulkConfirmationDialog>
  );
};
